<script setup>
import { ref, computed, watch } from 'vue'
import AudienceService from '@/services/AudienceService'
import Spinner from '@/components/ui/Spinner.vue'
import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import { MOBILE_SHEET_WIDTH } from './audienceDialog'

// Poll and Q&A results that outlived the room.
//
// Rooms wipe themselves after a week idle and a purge drops everything on the
// spot, so the live room card can only ever show what is still in the room.
// This reads the record the archival webhook builds alongside it, which is what
// a host wants right after clearing a room, or on coming back to a stream days
// later.
//
// An empty record is the ordinary answer, not a failure: nothing is archived
// until a poll closes or a stream ends.

const props = defineProps({
	streamId: {
		type: String,
		required: true
	}
})

const isVisible = defineModel({ type: Boolean, default: false })

const archive = ref(null)
const loading = ref(false)
const loadError = ref('')

watch(isVisible, (open) => {
	if (open) load()
})

const load = async () => {
	loading.value = true
	loadError.value = ''

	try {
		archive.value = await AudienceService.getArchive(props.streamId)
	} catch (err) {
		loadError.value = err.message || 'Could not load the archived results'
	}

	loading.value = false
}

const polls = computed(() => (archive.value && archive.value.polls) || [])
const questions = computed(() => (archive.value && archive.value.questions) || [])
const isEmpty = computed(() => !polls.value.length && !questions.value.length)

const updatedAt = computed(() => {
	const at = archive.value && archive.value.updatedAt
	return at ? new Date(at).toLocaleString() : ''
})

const formatDate = (ts) => (ts ? new Date(ts).toLocaleString() : '')

const totalVotes = (poll) => (poll.counts || []).reduce((sum, n) => sum + n, 0)

const share = (poll, index) => {
	const total = totalVotes(poll)
	if (!total) return 0
	return Math.round((((poll.counts || [])[index] || 0) / total) * 100)
}

/** Most upvoted first — the order the results are worth reading in. */
const rankedQuestions = computed(() => {
	return [...questions.value].sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0))
})
</script>

<template>
	<!-- A large dialog has no height cap of its own, and a long-running stream
	can archive more polls and questions than a laptop window is tall. Capping
	it lets scroll-body do its job instead of the dialog running off screen. -->
	<ModalBox v-model="isVisible" title="Archived results" size="lg" scroll-body
		:wrapper-classes="`${MOBILE_SHEET_WIDTH} lg:max-h-[80vh]`">
		<div class="px-4 pb-4 sm:px-6 sm:pb-6">
			<div v-if="loading" class="py-10">
				<Spinner />
			</div>

			<template v-else-if="loadError">
				<Alert type="error" :text="loadError" />
				<div class="flex justify-center mt-3">
					<FormButton type="secondary" size="sm" @click="load">Try again</FormButton>
				</div>
			</template>

			<div v-else-if="isEmpty" class="text-center py-8">
				<p class="text-sm text-surface-text-1 mb-1">Nothing archived for this stream yet.</p>
				<p class="text-tiny text-surface-7 max-w-[420px] mx-auto mb-0">
					Poll results are kept when a poll closes, and the full Q&A when a stream ends. They stay
					here after the room itself is cleared or expires, so results are not lost with it.
				</p>
			</div>

			<template v-else>
				<p v-if="updatedAt" class="text-tiny text-surface-7 mb-4">
					Last archived {{ updatedAt }}<span v-if="archive.endedAt">, stream ended
						{{ formatDate(archive.endedAt) }}</span>.
				</p>

				<section v-if="polls.length" class="mb-5">
					<h4 class="text-base mb-2">Polls ({{ polls.length }})</h4>

					<div v-for="poll in polls" :key="`${poll.id}:${poll.openedTs}`"
						class="bg-surface-2 rounded-lg p-3 mb-2">
						<div class="flex items-start justify-between gap-x-3 mb-2">
							<p class="text-sm font-medium text-surface-text-1 break-words mb-0">
								{{ poll.question }}
							</p>
							<span class="shrink-0 text-tiny text-surface-7 whitespace-nowrap"
								:title="formatDate(poll.closedTs || poll.openedTs)">
								{{ totalVotes(poll) }} votes
							</span>
						</div>

						<div v-for="(option, index) in poll.options" :key="index"
							class="relative overflow-hidden bg-surface-3 rounded-md mb-1.5">
							<div class="absolute inset-y-0 left-0 bg-main/30"
								:style="{ width: share(poll, index) + '%' }"></div>
							<div class="relative flex items-center justify-between px-3 py-1.5">
								<span class="text-sm text-surface-text-1 break-words">{{ option }}</span>
								<span class="text-tiny text-surface-7 whitespace-nowrap ml-3">
									{{ (poll.counts && poll.counts[index]) || 0 }} · {{ share(poll, index) }}%
								</span>
							</div>
						</div>
					</div>
				</section>

				<section v-if="rankedQuestions.length">
					<h4 class="text-base mb-2">Questions ({{ rankedQuestions.length }})</h4>

					<div v-for="question in rankedQuestions" :key="question.id"
						class="flex items-start gap-x-3 bg-surface-2 rounded-lg p-3 mb-2">
						<div
							class="flex flex-col items-center justify-center shrink-0 min-w-[42px] bg-surface-3 rounded-md px-2 py-1">
							<span class="text-sm font-medium leading-none">{{ question.upvotes || 0 }}</span>
							<span class="text-tiny text-surface-7 leading-none mt-0.5">votes</span>
						</div>

						<div class="min-w-0 flex-1">
							<p class="text-tiny text-surface-7 mb-0.5">
								{{ question.name || 'Guest' }}
								<span v-if="question.ts"> · {{ formatDate(question.ts) }}</span>
							</p>
							<p class="text-sm text-surface-text-1 break-words mb-0">{{ question.text }}</p>
							<span v-if="question.answered"
								class="inline-block text-tiny text-success mt-1">Answered</span>
						</div>
					</div>
				</section>
			</template>
		</div>

		<template #footer>
			<footer class="flex justify-end gap-2 border-t border-surface-3 p-4 sm:p-6">
				<FormButton type="secondary" @click="isVisible = false">Close</FormButton>
			</footer>
		</template>
	</ModalBox>
</template>
